import { apiClient, type AttachmentContentsResponse } from './api';
import { AttachmentCacheService } from './attachmentCache';

// Safe default chunk size to avoid URL length limits
const CHUNK_SIZE = 50;

// Helper: deduplicate IDs while preserving input type
const dedupeIds = (ids: string[]): string[] => Array.from(new Set(ids));

// Helper: split list into fixed-size chunks
const makeChunks = (ids: string[], size: number): string[][] => {
    const chunks: string[][] = [];
    for (let i = 0; i < ids.length; i += size) {
        chunks.push(ids.slice(i, i + size));
    }
    return chunks;
};

// Helper: get attachment contents for a chunk with cache and persistence
const getChunkContents = async (chunk: string[], accessToken?: string): Promise<AttachmentContentsResponse> => {
    const cachedData = await AttachmentCacheService.getCachedAttachments(chunk);
    if (cachedData) return cachedData;

    console.log("Fetching attachment contents from API", { attachmentIds: chunk, accessToken });
    const responseForChunk = await apiClient.fetchAttachmentContents(chunk, accessToken);
    await AttachmentCacheService.cacheAttachments(chunk, responseForChunk);
    return responseForChunk;
};

// Helper: merge items uniquely using a Set of seen IDs
const mergeUnique = (
    aggregated: AttachmentContentsResponse,
    seen: Set<string>,
    items: AttachmentContentsResponse | null | undefined
): void => {
    if (!items || items.length === 0) return;
    for (const item of items) {
        if (!seen.has(item.id)) {
            aggregated.push(item);
            seen.add(item.id);
        }
    }
};

export const attachmentApi = {

    async getAttachment(attachmentIds: string[], accessToken?: string): Promise<AttachmentContentsResponse> {
        console.log("Calling get attachment api with Attachment Ids", { attachmentIds });

        if (!attachmentIds || attachmentIds.length === 0) {
            return [];
        }

        // Deduplicate IDs first
        const uniqueIds = dedupeIds(attachmentIds);

        // Chunk IDs to avoid URL size limits (safe default ~50 IDs per request)
        const chunks = makeChunks(uniqueIds, CHUNK_SIZE);

        // Aggregate results while avoiding duplicates
        const aggregated: AttachmentContentsResponse = [];
        const seen = new Set<string>();

        // Fetch chunks: single request for one chunk, otherwise parallel with bounded concurrency
        if (chunks.length === 1) {
            const data = await getChunkContents(chunks[0], accessToken);
            mergeUnique(aggregated, seen, data);
        } else if (chunks.length > 1) {
            const CONCURRENCY = Math.min(6, chunks.length); // cap concurrency to avoid network saturation
            const results: AttachmentContentsResponse[] = new Array(chunks.length);
            let nextIndex = 0;

            const worker = async () => {
                while (true) {
                    const i = nextIndex++;
                    if (i >= chunks.length) break;
                    const data = await getChunkContents(chunks[i], accessToken);
                    results[i] = data;
                }
            };

            const workers: Promise<void>[] = [];
            for (let i = 0; i < CONCURRENCY; i++) workers.push(worker());
            await Promise.all(workers);

            for (const items of results) {
                mergeUnique(aggregated, seen, items);
            }
        }

        return aggregated;
    },

    /**
     * Clear attachment cache (for debugging)
     */
    async clearCache(): Promise<void> {
        await AttachmentCacheService.clearCache();
    },

    /**
     * Get cache statistics (for debugging)
     */
    async getCacheStats(): Promise<{ entryCount: number; totalSize: number; maxSize: number; maxEntries: number }> {
        return AttachmentCacheService.getCacheStats();
    }
};
